import { useStore } from 'zustand';

import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * Turning and resizing a selection by an exact amount.
 *
 * ## Why buttons, and when handles are right instead
 *
 * Every drawing program puts eight handles round a selection and lets you drag them. That is
 * the right interface for a poster and the wrong one for a geometry lesson, where the useful
 * amounts are *named*: thirty degrees, ninety degrees, half size, double. Dragging a corner
 * cannot say ninety degrees; it can only say "about ninety", and a figure that is nearly
 * rotated by a right angle is a figure that quietly teaches the wrong thing.
 *
 * So the amounts are on buttons, and each is one undo step.
 *
 * `TransformTool` now offers the handles as well, and it does not contradict any of the above —
 * it answers it. A turn there **snaps to fifteen degrees**, so the number the gesture produces
 * is exact rather than near, and the figure it has reached is written beside the cursor while
 * the drag runs. The pointer chooses which multiple; it does not choose what the multiple is.
 *
 * The two are for different acts and both are worth having. A user who already knows they want
 * ninety degrees should not have to find it with the mouse, and a user looking for the size that
 * fits should not have to guess a number and press a button until it does.
 *
 * ## Why there is no non-uniform scale here
 *
 * Stretching one axis turns a circle into an ellipse and a square into a rectangle. The
 * constructions underneath cannot express that: `circle-through` is defined by two points and
 * is a circle by construction, so a stretched drawing would silently stop matching the figure
 * it claims to be. Uniform scaling maps every shape to one of its own kind and keeps every
 * construction true, which is the property this editor is built to preserve.
 *
 * That limit belongs to the *selection*, not to every shape: a single `free-ellipse` stores its
 * two radii separately and can be stretched exactly. `TransformTool` offers edge handles in that
 * one case, and `domain/construction/reshape.ts` is where the distinction is drawn.
 */

const DEGREES = [-90, -45, -30, 30, 45, 90] as const;
const FACTORS = [
  { label: '÷2', value: 0.5 },
  { label: '×1.5', value: 1.5 },
  { label: '×2', value: 2 },
] as const;

export interface TransformPanelProps {
  readonly store: EditorStore;
}

export function TransformPanel({ store }: TransformPanelProps) {
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  // Shown only when there is something to act on. A panel of disabled buttons is furniture.
  if (selection.size === 0) return null;

  return (
    <PanelSection title="Biến đổi" badge={`${String(selection.size)} đối tượng`}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Xoay quanh tâm</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {DEGREES.map((degrees) => (
            <button
              key={degrees}
              type="button"
              onClick={() => {
                commands.transformSelection({ rotation: (degrees * Math.PI) / 180 });
              }}
              className="es-btn"
              style={buttonStyle}
            >
              {degrees > 0 ? `+${String(degrees)}°` : `${String(degrees)}°`}
            </button>
          ))}
        </div>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Phóng to / thu nhỏ</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {FACTORS.map(({ label, value }) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                commands.transformSelection({ scale: value });
              }}
              className="es-btn"
              style={buttonStyle}
            >
              {label}
            </button>
          ))}
        </div>
      </label>

      {/*
        Said out loud because it is the first thing a user tries and the first thing they would
        otherwise assume is broken.
      */}
      <p style={{ margin: 0, color: colour.muted, fontSize: 11 }}>
        Kéo để di chuyển cả nhóm. Phím mũi tên để dịch từng bước nhỏ.
      </p>
    </PanelSection>
  );
}

/** Layout only. Every appearance and every interaction state comes from `es-btn`. */
const buttonStyle = { padding: '5px 0', fontSize: 11 } as const;
